import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBrand } from "../context/BrandContext";
import { currentAppLanguage } from "../utils/appLanguages";
import { readActiveHomeRole, readStoredUser } from "../utils/homeRole";
import {
  resolveDigipingOpenRole,
  sanitizeProfilingIntroForRole,
} from "../utils/digipingOpenContext";
import { getHomeInsight } from "./funtionsBD/Mainservice";
import "./HomeDailyFocus.css";

export const OPEN_AI_COACH_EVENT = "smarttt:open-ai-coach";

/**
 * Dispatch open event for the global AICoachChat digital twin.
 * Uses the active home-role picker unless a player-only flow (technical profiling, …)
 * explicitly opens as player.
 */
export function openDigitalTwin({
  home_role,
  insightText,
  seedMessage,
  seedVisible = true,
  profilingIntro = null,
  /** i18n key — DigiPing re-resolves at send time in the current app language */
  seedKey = null,
  seedParams = null,
  composerPrefill = null,
  tab = null,
  grId = null,
} = {}) {
  let activeRole = "player";
  try {
    activeRole = readActiveHomeRole(readStoredUser()) || "player";
  } catch {
    activeRole = "player";
  }
  const role = resolveDigipingOpenRole({
    requestedRole: home_role || null,
    activeRole,
    profilingIntro,
  });
  const intro = sanitizeProfilingIntroForRole(role, profilingIntro);
  const dropPlayerOnlySeed = Boolean(profilingIntro && !intro);
  window.dispatchEvent(
    new CustomEvent(OPEN_AI_COACH_EVENT, {
      detail: {
        home_role: role,
        insightText: insightText || "",
        seedMessage: dropPlayerOnlySeed ? "" : seedMessage || "",
        seedVisible: seedVisible !== false,
        profilingIntro: intro,
        seedKey: dropPlayerOnlySeed ? null : seedKey || null,
        seedParams: dropPlayerOnlySeed ? null : seedParams || null,
        composerPrefill: composerPrefill || "",
        tab: tab || null,
        grId: grId || null,
      },
    })
  );
}

export function HomeDailyFocus({ homeRole = "player" }) {
  const { t, i18n } = useTranslation();
  const { brand } = useBrand();
  const showAiCoach = brand?.show_ai_coach !== false;
  const isPlayerHome = String(homeRole || "player").toLowerCase() === "player";
  const showTwinCta = showAiCoach && !isPlayerHome;
  const appLang = currentAppLanguage(i18n);
  const [loading, setLoading] = useState(true);
  const [insight, setInsight] = useState(null);
  const [error, setError] = useState(false);

  const fetchInsight = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getHomeInsight({
        home_role: homeRole,
        language: appLang,
      });
      const data = res?.data?.data;
      if (data?.message) {
        setInsight(data);
      } else {
        setInsight(null);
        setError(true);
      }
    } catch (_) {
      setInsight(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [homeRole, appLang]);

  useEffect(() => {
    fetchInsight();
  }, [fetchInsight]);

  const handleOpenTwin = () => {
    if (!showAiCoach) return;
    const text = insight?.message || "";
    const seed =
      t("homeDailyFocusSeed", {
        insight: text,
        defaultValue:
          "Based on this Daily Focus: \"{{insight}}\". Explain what I should do next to improve, progress, and manage my training activities. Teach me step by step.",
      }) || text;
    openDigitalTwin({
      home_role: homeRole,
      insightText: text,
      seedMessage: seed,
    });
  };

  return (
    <section
      className="home-daily-focus"
      aria-label={t("homeDailyFocusTitle") || "Daily Focus"}
    >
      <div className="home-daily-focus__inner">
        <div className="home-daily-focus__head">
          <span className="home-daily-focus__eyebrow">
            {t("homeDailyFocusTitle") || "Daily Focus"}
          </span>
          {insight?.source === "gemini" && (
            <span className="home-daily-focus__ai-badge">
              {t("homeDailyFocusAiBadge") || "Smart tip"}
            </span>
          )}
        </div>

        {loading ? (
          <div className="home-daily-focus__skeleton" aria-hidden="true">
            <span />
            <span />
          </div>
        ) : (
          <p className="home-daily-focus__message">
            {insight?.message ||
              (error
                ? t("homeDailyFocusFallback") ||
                  "Stay focused on your main goal and show up ready for the next session."
                : t("homeDailyFocusFallback") ||
                  "Stay focused on your main goal and show up ready for the next session.")}
          </p>
        )}

        {showTwinCta ? (
          <button
            type="button"
            className="home-daily-focus__cta"
            onClick={handleOpenTwin}
            disabled={loading}
          >
            {t("homeDailyFocusOpenTwin") || "Ask Digiping"}
          </button>
        ) : !isPlayerHome && !showAiCoach ? (
          <p className="home-daily-focus__hint">
            {t("homeDailyFocusTwinOff") ||
              "Digital twin coaching is disabled for this brand."}
          </p>
        ) : null}
      </div>
    </section>
  );
}
